"use client";

import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import type { SerializedEditorState } from "@payloadcms/richtext-lexical/lexical";
import type { PayloadMediaUpload } from "@portal/app/_server/services/formFunnel/payloadTypes";
import { FunnelButton, FunnelSubmitWrapper } from "./FunnelButton";
import { FunnelBackButton } from "./FunnelBackButton";
import { FormFunnelRichText } from "./FormFunnelRichText";
import { getFormFunnelImageProps } from "./formFunnelMedia";

export interface InterstitialStepProps {
  /** Lexical rich text JSON content (SerializedEditorState) */
  content?: SerializedEditorState | Record<string, unknown>;
  /** Featured image (populated Media upload object or URL string) */
  image?: PayloadMediaUpload | string;
  /** Called when the user clicks Continue */
  onContinue: () => void;
  /** Called when the user clicks Back (undefined if first step) */
  onBack?: () => void;
}

export const InterstitialStep = ({ content, image, onContinue, onBack }: InterstitialStepProps) => {
  const imageProps = getFormFunnelImageProps(image);

  return (
    <StepContainer role="region" aria-label="Information">
      {onBack && (
        <FunnelBackButton
          appearance="text"
          onClick={onBack}
          data-testid="interstitial-back-button"
          sx={{ alignSelf: "flex-start", mb: 1 }}
        >
          Back
        </FunnelBackButton>
      )}
      {imageProps && (
        <ImageWrapper>
          <img
            src={imageProps.src}
            alt={imageProps.alt}
            width={imageProps.width}
            height={imageProps.height}
            style={{
              maxWidth: "100%",
              maxHeight: 360,
              height: "auto",
              objectFit: "contain",
              display: "block",
            }}
          />
        </ImageWrapper>
      )}

      <FormFunnelRichText
        content={content}
        appearance="interstitial"
        promoteFirstHeading
        testId="interstitial-content"
      />

      <FunnelSubmitWrapper width="100%" sx={{ mt: 3, pt: 0 }}>
        <FunnelButton
          onClick={onContinue}
          data-testid="interstitial-continue-button"
          sx={{ maxWidth: "none", minHeight: 56, fontSize: "1.1rem" }}
        >
          Continue
        </FunnelButton>
      </FunnelSubmitWrapper>
    </StepContainer>
  );
};

const StepContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ImageWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 480px;
  margin-bottom: 24px;
  border-radius: 12px;
  overflow: hidden;
`;
